import DetachButton from '@/features/study-detail/DetachButton/DetachButton';
import { useState, useRef, useEffect } from 'react';
import { usePermissions } from '@/hooks/usePermissions';
import { useGetStudyReportsQuery } from '@/state/slices/reports';
import {
  ToolOutlined,
  DragOutlined,
  ZoomInOutlined,
  BgColorsOutlined,
  DashboardOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { RiResetLeftLine } from 'react-icons/ri';
import {
  PanTool,
  ZoomTool,
  WindowLevelTool,
  LengthTool,
  AngleTool,
  PlanarFreehandROITool,
} from '@cornerstonejs/tools';
import {
  SPECTRAL_DOPPLER_TOOL_NAMES,
  type SpectralDopplerAvailability,
} from '@/features/study-detail/Viewer/tools/SpectralDoppler';
import { SIMPSON_TOOL_NAME } from '@/utils/measurementToolMapping';
import { ToolbarTooltip } from './ToolbarTooltip';
import styles from './ViewerControls.module.css';
import { MultiViewerOptionButton } from '../MultiViewerOptionButton';
import { useViewerContext, type GridLayout } from '../ViewerContext';
import { MeasurementPopover } from './MeasurementPopover';
import { CineFpsPopover } from './CineFpsPopover';

const MEASUREMENT_TOOLS: readonly string[] = [
  LengthTool.toolName,
  AngleTool.toolName,
  PlanarFreehandROITool.toolName,
  SIMPSON_TOOL_NAME,
  SPECTRAL_DOPPLER_TOOL_NAMES.PROBE,
  SPECTRAL_DOPPLER_TOOL_NAMES.SEGMENT,
  SPECTRAL_DOPPLER_TOOL_NAMES.VTI,
  SPECTRAL_DOPPLER_TOOL_NAMES.TIME,
];

interface ViewerFrameControlsProps {
  /** Native FPS of the active clip; anchors the frame-rate slider. */
  nativeFps: number;
  /** Playback speed relative to native (1 = native rate). */
  cineSpeedMultiplier: number;
  onCineSpeedMultiplierChange: (multiplier: number) => void;
  isDetached: boolean;
  onToggleDetach: () => void;
  onDetachHover?: () => void;
  detachDisabled?: boolean;
  activeTool?: string;
  onToolChange?: (tool: string) => void;
  gridLayout?: GridLayout;
  onGridLayoutChange?: (layout: GridLayout) => void;
  onResetViewport?: () => void;
  spectralDopplerAvailability?: SpectralDopplerAvailability;
}

export function ViewerControls({
  nativeFps,
  cineSpeedMultiplier,
  onCineSpeedMultiplierChange,
  isDetached,
  onToggleDetach,
  onDetachHover,
  detachDisabled,
  activeTool,
  onToolChange,
  gridLayout,
  onGridLayoutChange,
  onResetViewport,
  spectralDopplerAvailability,
}: ViewerFrameControlsProps) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [fpsPopoverOpen, setFpsPopoverOpen] = useState(false);
  const popoverWrapperRef = useRef<HTMLDivElement>(null);
  const fpsPopoverWrapperRef = useRef<HTMLDivElement>(null);
  // A finalized report hides the measurement tools, as for read-only roles.
  const { studyUuid } = useViewerContext();
  const { data: reports } = useGetStudyReportsQuery(studyUuid, {
    skip: !studyUuid,
  });
  const { canEdit } = usePermissions({
    isFinalized: !!reports?.results?.[0]?.isFinalized,
  });

  const isMeasurementToolActive =
    !!activeTool && MEASUREMENT_TOOLS.includes(activeTool);

  useEffect(() => {
    if (!popoverOpen && !fpsPopoverOpen) return undefined;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        popoverWrapperRef.current &&
        !popoverWrapperRef.current.contains(target)
      ) {
        setPopoverOpen(false);
      }
      if (
        fpsPopoverWrapperRef.current &&
        !fpsPopoverWrapperRef.current.contains(target)
      ) {
        setFpsPopoverOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [popoverOpen, fpsPopoverOpen]);

  const handleToolClick = (toolName: string) => {
    onToolChange?.(toolName);
  };

  return (
    <div className={styles.toolbarContainer}>
      <DetachButton
        isDetached={isDetached}
        onToggle={onToggleDetach}
        onMouseEnter={onDetachHover}
        disabled={detachDisabled}
      />

      <ToolbarTooltip title="Pan image">
        <button
          type="button"
          className={styles.toolButton}
          data-active={activeTool === PanTool.toolName}
          onClick={() => handleToolClick(PanTool.toolName)}
          aria-label="Pan image"
        >
          <DragOutlined />
        </button>
      </ToolbarTooltip>

      <ToolbarTooltip title="Zoom image">
        <button
          type="button"
          className={styles.toolButton}
          data-active={activeTool === ZoomTool.toolName}
          onClick={() => handleToolClick(ZoomTool.toolName)}
          aria-label="Zoom image"
        >
          <ZoomInOutlined />
        </button>
      </ToolbarTooltip>

      <ToolbarTooltip title="Adjust brightness and contrast">
        <button
          type="button"
          className={styles.toolButton}
          data-active={activeTool === WindowLevelTool.toolName}
          onClick={() => handleToolClick(WindowLevelTool.toolName)}
          aria-label="Adjust brightness and contrast"
        >
          <BgColorsOutlined />
        </button>
      </ToolbarTooltip>

      {canEdit && (
        <div className={styles.popoverWrapper} ref={popoverWrapperRef}>
          <ToolbarTooltip
            title="Select measurement tool"
            open={popoverOpen ? false : undefined}
          >
            <button
              type="button"
              className={styles.toolButton}
              data-active={isMeasurementToolActive}
              onClick={() => setPopoverOpen((open) => !open)}
              aria-label="Select measurement tool"
            >
              <ToolOutlined />
            </button>
          </ToolbarTooltip>

          <MeasurementPopover
            open={popoverOpen}
            onClose={() => setPopoverOpen(false)}
            activeTool={activeTool}
            onToolChange={onToolChange}
            spectralDopplerAvailability={spectralDopplerAvailability}
          />
        </div>
      )}

      {gridLayout && onGridLayoutChange && (
        <MultiViewerOptionButton
          selectedLayout={gridLayout}
          onLayoutChange={onGridLayoutChange}
        />
      )}

      <div className={styles.popoverWrapper} ref={fpsPopoverWrapperRef}>
        <ToolbarTooltip
          title="Adjust frame rate"
          open={fpsPopoverOpen ? false : undefined}
        >
          <button
            type="button"
            className={styles.fpsButton}
            data-active={fpsPopoverOpen}
            onClick={() => setFpsPopoverOpen((open) => !open)}
            aria-label="Adjust frame rate"
          >
            <DashboardOutlined />
            <DownOutlined className={styles.fpsChevron} />
          </button>
        </ToolbarTooltip>

        <CineFpsPopover
          open={fpsPopoverOpen}
          nativeFps={nativeFps}
          cineSpeedMultiplier={cineSpeedMultiplier}
          onCineSpeedMultiplierChange={onCineSpeedMultiplierChange}
        />
      </div>

      {onResetViewport && (
        <>
          <div className={styles.divider} aria-hidden="true" />
          <ToolbarTooltip title="Reset viewer">
            <button
              type="button"
              className={styles.toolButton}
              onClick={onResetViewport}
              aria-label="Reset viewer"
            >
              <RiResetLeftLine />
            </button>
          </ToolbarTooltip>
        </>
      )}
    </div>
  );
}
